import { DeactivateLearningBudgetConfirmationModal } from '@/modules/learning-budgets/components/DeactivateLearningBudgetConfirmationModal';
import { yupResolver } from '@hookform/resolvers/yup';
import { OpenInNew } from '@mui/icons-material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

export type LearningBudgetFormData = {
  learningBudget: boolean;
};

type LearningBudgetFormProps = Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  'onSubmit'
> & {
  initialData: LearningBudgetFormData;
  onSubmit: (data: LearningBudgetFormData) => Promise<void>;
  onCreateNewCycle: () => Promise<void>;
  isLoading?: boolean;
};

const LearningBudgetFormSchema = yup.object().shape({
  learningBudget: yup.boolean().required(),
});

export function LearningBudgetForm({
  initialData,
  onSubmit,
  onCreateNewCycle,
  isLoading = false,
  ...props
}: LearningBudgetFormProps) {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<LearningBudgetFormData>({
    disabled: isLoading,
    values: initialData,
    resolver: yupResolver(LearningBudgetFormSchema),
  });

  const [
    isDeactivateLearningBudgetModalOpen,
    setIsDeactivateLearningBudgetModalOpen,
  ] = useState(false);

  return (
    <>
      <form
        {...props}
        onSubmit={handleSubmit(onSubmit)}
        className={`flex flex-col w-full gap-4 ${props.className ?? ''}`}
      >
        <header className="flex gap-2 justify-between">
          <div>
            <Typography variant="h3" component="h2">
              Learning Budget
            </Typography>
            <Typography variant="body1" color="text.secondary" className="mt-1">
              Configure how your learning budget works.
            </Typography>
          </div>
          <div className="flex gap-2 items-start">
            <Button
              href={'/employees'}
              target="_blank"
              startIcon={<OpenInNew />}
              variant="outlined"
            >
              Manage employees
            </Button>
            <Button
              href="/employees?tab=teams"
              target="_blank"
              startIcon={<OpenInNew />}
              variant="outlined"
            >
              Manage teams
            </Button>
            <Button
              onClick={onCreateNewCycle}
              startIcon={<AccountBalanceWalletOutlinedIcon />}
            >
              Create new cycle
            </Button>
          </div>
        </header>
        <div className="flex flex-col flex-1 flex-grow">
          <div className="flex flex-1 gap-2 items-center align-center">
            <FormControlLabel
              control={
                <Controller
                  name="learningBudget"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="learningBudget"
                      checked={field.value}
                      disabled={isSubmitting}
                      onChange={e => {
                        if (!e.target.checked) {
                          setIsDeactivateLearningBudgetModalOpen(true);
                          return;
                        } else {
                          field.onChange(e.target.checked);
                          handleSubmit(onSubmit)();
                        }
                      }}
                    />
                  )}
                />
              }
              label={
                <label htmlFor="learningBudget" className="font-semibold">
                  Enable Learning Budget feature
                </label>
              }
            />
          </div>
          <label
            htmlFor="learningBudget"
            className="mt-2 text-sm text-gray-500"
          >
            If enabled, employees can request course purchases using their
            learning budget, subject to your approval.
          </label>
        </div>
      </form>
      <DeactivateLearningBudgetConfirmationModal
        open={isDeactivateLearningBudgetModalOpen}
        onClose={async ({ confirmed }) => {
          if (confirmed) {
            await onSubmit({ learningBudget: false });
          }
          setIsDeactivateLearningBudgetModalOpen(false);
        }}
      />
    </>
  );
}
